import { generateNextCopyName } from '@core/util/copy-utils';
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Injectable} from "@angular/core";
import { mapConfigActions } from '../actions'
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from "rxjs";
import {Store} from "@ngrx/store";
import {State} from "../reducers/map-config.reducer";
import {State as mapFilesSatate} from "../reducers/map-files.reducer";
import {State as datacentersState} from "../reducers/datacenter.reducer";
import {State as providersState} from "../reducers/provider.reducer";
import {MapLine, MapPolygon} from "../../model/summary";
import {UserMap} from "../../model/user-map";
import {MapConfigStateService} from "../../service/map-config-state.service";
import {MapService} from "../../service/map.service";
import {DataCentersService} from "../../service/data-centers.service";
import * as _ from "lodash";
import {AppStateService} from "../../service/app-state.service";
import {MapFile} from "../../model/map-file";
import {ACCESS_CONFIG} from "@core/consts";
import {Folder} from "@core/model/folder";


@Injectable()
export class MapConfigEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store,
    public mapConfigState: MapConfigStateService,
    private mapService: MapService,
    private dataCentersService: DataCentersService,
    private appStateService: AppStateService
  ) {
  }

  saveMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.saveMap),
      withLatestFrom(this.store),
      map(([action, store]) => {
          const currentState = (store as any).mapConfig as State;
          const currentMapFilesState = (store as any).mapFiles as mapFilesSatate;
          const currentDatacentersState = (store as any).datacenters as datacentersState;
          const currentProvidersState = (store as any).providers as providersState;
          const userMap = _.cloneDeep(currentState.userMap);
          userMap.modifiedBy = this.appStateService.user.userId
          userMap.units = currentState.units;
          userMap.datacenters = _.cloneDeep(currentDatacentersState.changedDatacenters);
          userMap.mapPolygons = [];
          userMap.mapLines = [];
          userMap.mwThreshold = currentState.mwThreshold;
          userMap.polygons = JSON.stringify(action.polygons);
          userMap.providers = currentProvidersState.changedProviders;
          userMap.mapLabels = action.labels;
          //create dtos from state
          for (const feature of currentState.drawingFeatures.features) {
            if (feature.geometry.type == "LineString") {
              const geometry = feature.geometry as any;
              console.log({
                label: feature.properties['label']
              })
              const mapLine: MapLine = {
                auxId: feature.id.toString(),
                label: feature.properties['label'],
                pointA: [geometry.coordinates[0][0], geometry.coordinates[0][1]],
                pointB: [geometry.coordinates[1][0], geometry.coordinates[1][1]],
              };
              userMap.mapLines.push(mapLine);
            } else {
              const geometry = feature.geometry as any;
              const mapPolygon: MapPolygon = {
                polygon: geometry.coordinates,
                auxId: feature.id.toString(),
                label: feature.properties['label'],
                color: feature.properties['color'],
                opacity: feature.properties['opacity'],
                circleRadius: feature.properties['circleRadius']
              };
              userMap.mapPolygons.push(mapPolygon);
            }
          }

          if (currentState.mapLines.length) {
            userMap.mapLines = userMap.mapLines.concat(currentState.mapLines);
          }
          if (currentState.mapPolygons.length) {
            userMap.mapPolygons = userMap.mapPolygons.concat(currentState.mapPolygons);
          }
          if (!!currentMapFilesState.mapFiles?.length) {
            userMap.files = currentMapFilesState.mapFiles.filter((mapFile: MapFile) => mapFile.fileAssets.some(asset => asset.selected))
              .map((mapFile: MapFile) => {
                return {
                  fileId: mapFile.fileId,
                  fileAssets: mapFile.fileAssets.filter(asset => asset.selected)
                };
              });
          }
          return userMap;
        }
      ),
      exhaustMap(userMap => this.mapService.saveUserMap(userMap).pipe(
        map(response => mapConfigActions.saveMapSuccess({userMap: response})),
        catchError((error: any) => of(mapConfigActions.saveMapError({msg: "error"})))
      ))
    )
  );

  savePartialMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.savePartialMap),
      exhaustMap(action => this.mapService.savePartialUserMap(action.userMap).pipe(
        map(response => mapConfigActions.saveMapSuccess({userMap: null})),
        catchError((error: any) => of(mapConfigActions.saveMapError({msg: "error"})))
      ))
    )
  );

  mergeMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.mergeMap),
      exhaustMap(action => this.mapService.mergeMap(action.maps, action.userMap, action.objectsToAdd).pipe(
        map(response => mapConfigActions.mergeMapSuccess({userMap: response})),
        catchError((error: any) => of(mapConfigActions.mergeMapError({msg: "error"})))
      ))
    )
  );

  loadUserMaps = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.loadUserMaps),
      exhaustMap(action => this.mapService.getUserMaps().pipe(
        map(response => mapConfigActions.loadUserMapsSuccess({userMaps: response})),
        catchError((error: any) => of(mapConfigActions.loadUserMapsError({msg: "error"})))
      ))
    )
  );

  uploadCsv = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.uploadCsv),
      exhaustMap(action => this.mapService.processCsv(action.formData).pipe(
        map(response => mapConfigActions.uploadCsvSuccess({userMap: response})),
        catchError((error: any) => of(mapConfigActions.uploadCsvError({msg: error.error.data})))
      ))
    )
  );

  copyFromMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.copyFromMap),
      exhaustMap(action => this.mapService.copyFromMap(action.mapId, action.maptoCopy).pipe(
        map(response => mapConfigActions.copyFromMapSuccess({userMap: response})),
        catchError((error: any) => of(mapConfigActions.copyFromMapError({msg: "error"})))
      ))
    )
  );

  duplicateFromMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.duplicateFromMap),
      exhaustMap(action =>
        this.mapService
          .getUserMap(action.userId, action.mapId, true)
          .pipe(
            switchMap((map: UserMap) => {
              const newUserMap = _.cloneDeep(action.userMap);
              newUserMap.mapName = generateNextCopyName(map.mapName);
              newUserMap.desc = map.desc;
              newUserMap.folderId = action.folderId;
              return this.mapService.mergeMap(
                [map],
                newUserMap,
                action.objectsToAdd
              )
            }),
            map((response: UserMap) => {
              return mapConfigActions.duplicateFromMapSuccess({
                userMap: response
              });
            }),
            catchError((error: any) =>
              of(mapConfigActions.duplicateFromMapError({ msg: 'error' }))
            )
          )
      )
    )
  );

  loadUserMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.loadUserMap),
      exhaustMap(action => this.mapService.getUserMap(action.userId, action.mapId, action.full).pipe(
        map(response => {
          let fullAccess = ACCESS_CONFIG.FULL_ACCESS_ENABLED;
          const userMap = response as UserMap;

          return mapConfigActions.loadUserMapSuccess({
            userMap: userMap,
            readOnly: !fullAccess
          });
        }),
        catchError((error: any) => of(mapConfigActions.loadUserMapError({msg: "error"})))
      ))
    )
  );

  loadFolders = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.loadFolders),
      exhaustMap(action => this.mapService.getFolders().pipe(
        map(response => {
          return mapConfigActions.loadFoldersSuccess({folders: response as Folder[]});
        }),
        catchError((error: any) => of(mapConfigActions.loadUserMapError({msg: "error"})))
      ))
    )
  );

  saveFolders = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.saveFolders),
      exhaustMap(action => this.mapService.saveFolders(action.folders).pipe(
        map(response => {
          return mapConfigActions.saveFoldersSuccess({folders: response as Folder[]});
        }),
        catchError((error: any) => of(mapConfigActions.saveFoldersError({error: "error"})))
      ))
    )
  );

  deleteMap = createEffect(() =>
    this.actions$.pipe(
      ofType(mapConfigActions.deleteMap),
      exhaustMap(action => this.mapService.deleteUserMap(action.mapId, action.folderId).pipe(
        map(response => {
          return mapConfigActions.deleteMapSuccess({mapId: action.mapId});
        }),
        catchError((error: any) => of(mapConfigActions.deleteMapError({error: "error"})))
      ))
    )
  );
}
